/// <reference types="@emotion/react/types/css-prop" />
import {ContentModule, useShowInstructions} from '@backstage-components/base';
import {css} from '@emotion/react';
import {useSubscription} from 'observable-hooks';
import {FC, useEffect, useRef, useState} from 'react';
import * as Styled from './Slideshow.styled';
import {ComponentDefinition, SchemaType} from './SlideshowDefinition';
import {MutedIcon, UnMutedIcon} from './assets/MuteIcon';
import * as glMain from './webgl/main';

export type SlideshowComponentDefinition = ContentModule<
  'Slideshow',
  SchemaType
>;

export const SlideshowComponent: FC<SlideshowComponentDefinition> = (
  definition
) => {
  const domRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const {props} = definition;
  const {
    audioURL: url,
    audioVolume: volume,
    audioLoop: loop = false,
    initMuted = false,
  } = props;

  const [isVisible, setIsVisible] = useState<boolean>(true);
  const [canvasClicked, setCanvasClicked] = useState<boolean>(false);
  const [audioReady, setAudioReady] = useState<boolean>(false);
  // muted state is used to keep track of the true muted state of the audio
  const [muted, setMuted] = useState<boolean>(initMuted);
  // mutedState is used to keep track of the users preference for muted
  const [mutedState, setMutedState] = useState<boolean>(initMuted);
  const [audio, setAudio] = useState<HTMLAudioElement | null>(null);

  /**
   * Create audio element if url is provided
   * cleanup on unmount
   */
  useEffect(() => {
    if (url) {
      setAudio((current) => {
        if (audio && audio.getAttribute('src') === url) {
          return current;
        } else {
          setAudioReady(false);
          const next = new Audio(url);
          return next;
        }
      });
    }
    return () => {
      if (audio) {
        audio.pause();
        audio.setAttribute('src', '');
        setAudio(null);
      }
    };
  }, [url, audio]);

  /**
   * Handle audio play/pause (mute) and volume
   */
  useEffect(() => {
    if (!audio) return;
    audio.volume = volume;
    audio.loop = loop;
    audio.oncanplaythrough = () => {
      setAudioReady(true);
    };
    audio.onloadeddata = () => {
      if (!isVisible) {
        audio.pause();
      }
    };

    // toggle audio play/pause based on muted state
    if (muted) {
      audio.pause();
    } else {
      audio.play();
    }

    /**
     * Handle case where audio is paused due to lack of browser interaction
     * Handle case where muted is set to false but the audio is still paused due to audio load
     */
    let timeout: NodeJS.Timeout | undefined;
    if (!muted && audio.paused) {
      audio.play();

      timeout = setTimeout(() => {
        if (audio.paused) {
          setMuted(true);
        }
      }, 250);
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [audio, volume, loop, muted, isVisible]);

  const {observable} = useShowInstructions(
    ComponentDefinition.instructions,
    definition
  );
  useSubscription(observable, (inst): void => {
    switch (inst.type) {
      case 'Slideshow:toggleVisibility': {
        setIsVisible((current) => {
          if (current) {
            setMutedState(muted);
            setMuted(true);
            glMain.toggleVisibility(false);
          } else {
            setMuted(mutedState);
            glMain.toggleVisibility(true);
          }

          return !current;
        });
        break;
      }
      case 'Slideshow:show': {
        setIsVisible(true);
        setMuted(mutedState);
        break;
      }
      case 'Slideshow:hide': {
        setIsVisible(false);
        setMutedState(muted);
        setMuted(true);
        break;
      }
      default:
        break;
    }
  });

  useEffect(() => {
    if (domRef.current && canvasRef.current && isVisible) {
      glMain.init(domRef.current, canvasRef.current, props);
    }

    if (!isVisible) {
      glMain.dispose();
    }

    return () => {
      glMain.dispose();
    };
  }, [props, isVisible]);

  const onCanvasClick = (): void => {
    //only do canvas click once
    if (!canvasClicked) {
      setCanvasClicked(true);
      setMuted(false);
    }
  };

  const toggleMuted = (): void => {
    setMuted(!muted);
  };

  const styles = css`
    ${definition.style}
    ${definition.props.styleAttr}
  `;

  return (
    <Styled.Wrapper css={styles} ref={domRef}>
      {props.audioURL && audioReady && isVisible ? (
        <Styled.MuteButton
          onClick={toggleMuted}
          type="button"
          aria-label={muted ? 'unmute' : 'mute'}
          aria-pressed={muted}
          data-testid="mute-button"
        >
          {muted ? <MutedIcon /> : <UnMutedIcon />}
        </Styled.MuteButton>
      ) : null}
      {isVisible ? <canvas ref={canvasRef} onClick={onCanvasClick} /> : null}
    </Styled.Wrapper>
  );
};
